// Modules
import React, { useContext } from "react";
import PropTypes from "prop-types";
import Plot from "react-plotly.js";

// Context
import ApiContext from "../../../../contexts/ApiContext";

// Local
import GraphProgressBar from "./GraphProgressbar";

// Assets
import "./graphs.scss";
import { Skeleton } from "@material-ui/lab";

const Graphs = (props) => {
	const {
		graphs,
		loader,
	} = props;
	const { isMobile, isTablet } = useContext(ApiContext);
	const config = {
		displayModeBar: false,
		responsive: true,
	};

	// Variables for graphs
	const vh = parseFloat(document.documentElement.style.getPropertyValue("--vh").replace("px", ""));
	const graphsContainer = document.getElementById("graphs-container");

	return (
		<React.Fragment>
			<div className="fourth-row" id="graphs-container">
				{/* EVOLUTION_GAIN_NET */}
				<Plot
					data={[
						{
							...graphs.evolution_gain_net?.data[0],
							mode: "lines",
							type: "scatter",
							hovertemplate: `Gain net :<br />%{y} €<extra></extra>`,
							line: {
								color: "#EEDFD2",
								width: "3",
							},
							textfont: {
								family: "var(--OpenSans)",
								color: "#0A313E",
							},
						},
						{
							...graphs.evolution_gain_net?.data[1],
							mode: "markers",
							type: "scatter",
							hovertemplate: `Cashflow :<br />%{y} €<extra></extra>`,
							marker: {
								color: "#F7B65B",
								size: "6",
							},
							textfont: {
								family: "var(--OpenSans)",
								color: "#0A313E",
							}
						},
						{
							...graphs.evolution_gain_net?.data[2],
							mode: "markers",
							type: "scatter",
							hovertemplate: `Amortissement:<br />%{y} €<extra></extra>`,
							marker: {
								color: "#A0BBC3",
								size: "6",
							},
							textfont: {
								family: "var(--OpenSans)",
								color: "#0A313E",
							}
						},
					]}
					layout={{
						...graphs.evolution_gain_net?.layout,
						title: {
							text: isMobile === true || isTablet === true
								? "Évolution du gain net et du cashflow<br />en fonction de la durée de possession"
								: "Évolution du gain net et du cashflow en fonction de la durée de possession",
							font: {
								family: "var(--PlayfairBold)",
								size: 2 * vh,
							},
						},
						font: { family: "var(--OpenSans)" },
						showlegend: false,
						xaxis: {
							title: "Durée de revente (en années)",
							showgrid: false,
							zeroline: false,
						},
						yaxis: {
							title: "Gain (en €)",
							hoverformat: ",.0f",
						},
						shapes: [{
							...graphs.evolution_gain_net?.layout?.shapes[0],
							type: "line",
							line: {
								color: "#0A313E",
								dash: "dash",
								width: 1,
							},
						}],
						width: isMobile === true || isTablet === true
							? graphsContainer?.offsetWidth + 100
							: graphsContainer?.offsetWidth - 100,
						height: 50 * vh,
					}}
					config={config}
					className={`evolution-gain-net ${loader === true || graphs.evolution_gain_net?.data?.length === 0 ? "loading" : ""}`}
				/>
				{loader === true || graphs.evolution_gain_net?.data?.length === 0
					? (
						<Skeleton
							variant="rect"
							width="90vh"
							height="45vh"
							className="skeleton evolution-gain-net"
						/>
					) : null}
			</div>

			{/* COMPARATIF_MENSUALITE_LOYER */}
			<div className="fifth-row">
				<GraphProgressBar data={graphs.comparatif_mensualite_loyer} loading={loader} />
			</div>

			<div className="six-row">

				{/* COMPARATIF_LIVRET_A */}
				<Plot
					className={`comparatif-livret-a ${loader === true || graphs.comparatif_livret_a?.data?.length === 0 ? "loading" : ""}`}
					data={[
						{
							type: "bar",
							x: ["Gain net du Livret A"],
							y: [graphs.comparatif_livret_a?.data[0]?.y[0]],
							hovertemplate: `Gain net du Livret A :<br />%{y} €<extra></extra>`,
							marker: {
								color: "#F7B65B",
							},
						},
						{
							type: "bar",
							x: ["Gain net de l'investissement"],
							y: [graphs.comparatif_livret_a?.data[0]?.y[1]],
							hovertemplate: `Gain net de l'investissement :<br />%{y} €<extra></extra>`,
							marker: {
								color: "#EEDFD2",
							},
						},
					]}
					layout={{
						title: {
							text: isMobile === true || isTablet === true
								? "Comparatif Livret A et<br />investissement sur 20 ans"
								: "Comparatif Livret A et investissement sur 20 ans",
							font: {
								family: "var(--PlayfairBold)",
								size: 2 * vh,
							},
						},
						font: { family: "var(--OpenSans)" },
						showlegend: false,
						yaxis: {
							zeroline: false,
							gridwidth: 2,
							automargin: true,
							hoverformat: ",.0f",
						},
						bargap: 0.20,
						paper_bgcolor: 'rgba(0,0,0,0)',
						plot_bgcolor: 'rgba(0,0,0,0)',
						width: isMobile === true || isTablet === true
							? graphsContainer?.offsetWidth
							: graphsContainer?.offsetWidth / 2,
						height: 50 * vh,
					}}
					config={config}
				/>

				{/* LEVIER_BANCAIRE */}
				<div
					className="levier-bancaire-graph"
					style={{ 
						width: (isMobile === true || isTablet === true) && isNaN(graphsContainer?.offsetWidth) === false 
							? graphsContainer?.offsetWidth 
							: (isMobile === false || isTablet === false) && isNaN(graphsContainer?.offsetWidth) === false 
								? graphsContainer?.offsetWidth / 2 
								: 0,
							opacity: loader === true || graphs.levier_bancaire?.data?.length === 0 ? 0 : 1,
						}}
				>
					<h6 className="title">Effet de levier bancaire</h6>
					<div className="graph-wrapper">
						<Plot
							className={`levier-bancaire ${loader === true || graphs.levier_bancaire?.data?.length === 0 ? "loading" : ""}`}
							data={[
								{
									type: "bar",
									x: [""],
									// x: [`Prix total de l'acquisition : ${new Intl.NumberFormat(
									// 	"fr-FR",
									// 	{ style: "currency", currency: "EUR", maximumFractionDigits: 0, minimumFractionDigits: 0 }
									// ).format(graphs.levier_bancaire?.data[0]?.y[1])}`],
									y: [graphs.levier_bancaire?.data[0]?.y[0]],
									hovertemplate: [`%{y} €<extra></extra>`],
									marker: {
										color: "#0A313E"
									},
								},
								{
									type: "bar",
									x: [""],
									// x: [`Prix total de l'acquisition : ${new Intl.NumberFormat(
									// 	"fr-FR",
									// 	{ style: "currency", currency: "EUR", maximumFractionDigits: 0, minimumFractionDigits: 0 }
									// ).format((graphs.levier_bancaire?.data[0]?.y[1]))}`],
									y: [graphs.levier_bancaire?.data[1]?.y[0]],
									hovertemplate: [`%{y} €<extra></extra>`],
									marker: {
										color: "#A0BBC3"
									},
								},
							]}
							layout={{
								// title: {
								// 	text: "Effet de levier bancaire",
								// 	font: {
								// 		family: "var(--PlayfairBold)",
								// 		size: isMobile === true || isTablet === true
								// 			? 1.7 * vh
								// 			: 2 * vh,
								// 	},
								// },
								font: { family: "var(--OpenSans)" },
								barmode: "stack",
								showlegend: false,
								yaxis: {
									zeroline: false,
									gridwidth: 2,
									automargin: true,
									hoverformat: ",.0f",
								},
								bargap: 0.20,
								paper_bgcolor: "rgba(0,0,0,0)",
								plot_bgcolor: "rgba(0,0,0,0)",
								// width: isMobile === true || isTablet === true
								// 	? graphsContainer?.offsetWidth
								// 	: graphsContainer?.offsetWidth / 2,
								width: isMobile === true || isTablet === true
									? graphsContainer?.offsetWidth / 1.5
									: graphsContainer?.offsetWidth / 3,
								height: 50 * vh,
							}}
							config={config}
						/>
						<div className="fake-bar-container">
							<div className="fake-tooltip-wrapper">
								<p>
									{new Intl.NumberFormat
										(
											"fr-FR",
											{ style: "currency", currency: "EUR", maximumFractionDigits: 0, minimumFractionDigits: 0 }
										).format((graphs.levier_bancaire?.data[0]?.y[1]))
									}
								</p>
							</div>
							<span className="fake-bar" />
						</div>
					</div>
					<ul className={`legend ${loader === true || graphs.levier_bancaire?.data?.length === 0 ? "loading" : ""}`}>
						<li>
							<span className="color" />
							<p>Mise de départ</p>
						</li>
						<li>
							<span className="color" />
							<p>Montant emprunté</p>
						</li>
						<li>
							<span className="color" />
							<p>Total acquisition</p>
						</li>
					</ul>
				</div>

				{loader === true
					|| graphs.comparatif_livret_a?.data?.length === 0
					|| graphs.levier_bancaire?.data?.length === 0
					? (
						<React.Fragment>
							<Skeleton
								variant="rect"
								width="48vh"
								height="45vh"
								className="skeleton comparatif-livret-a"
							/>
							<Skeleton
								variant="rect"
								width="48vh"
								height="45vh"
								className="skeleton levier-bancaire"
							/>
						</React.Fragment>
					) : null}
			</div>
		</React.Fragment>
	);
};

Graphs.propTypes = {
	graphs: PropTypes.object.isRequired,
	loader: PropTypes.bool.isRequired,
};

export default Graphs;